/* This module supports Invoice, Quote, and Pick Ticket printer selection.
   The backordering process has its own dedicated selection modal (see PrinterSelectionBOModal).
*/

import _ from 'lodash';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button, Modal, FormCheck } from 'react-bootstrap';
import classNames from 'classnames';
import moment from 'moment';
import toastr from '../../../toastr';
import { Api } from '../../typings/Api';
import Pikaday from '../../../report/vendor/pikaday/pikaday-dsi';

interface IProps {
  activePrinterDefault: { id: string; name: string } | null; // last used (local storage)
  container: Object;
  quoteParams?: { defaultDays: number; maxDays: number; hidePartNumbers: boolean };
  hide: () => void;
  onPrinterSelection: (
    id: string,
    name: string,
    expirationDate?: string,
    hidePartNumbers?: boolean,
    quoteTitle?: string
  ) => void;
  printerList: Array<{ id: string; name: string }>;
  screenPrinterID: string | null;
  show: boolean;
}

const PrintSelectionModal: FunctionComponent<IProps> = ({
  activePrinterDefault,
  container,
  quoteParams,
  hide,
  onPrinterSelection,
  printerList,
  screenPrinterID,
  show,
}) => {
  const [activePrinter, setActivePrinter] = useState(() => {
    const initialState = {
      id: '',
      name: '',
    };

    /* Confirm any passed active printer (last used, saved in local storage)
       still exists in the current prinerList.  Legacy may have changed ids/name.
       If it exists, give color feedback, and allow the user to enter qickly
       past the modal should he/she not wish to change the selection.

       In the event a sole printer is defined, select it by default (this case should
       only happen via the quote path, as other paths skip the pop up modal when only
       one printer is defined).
    */
    if (printerList.length === 1) {
      activePrinterDefault = printerList[0];
    }

    if (activePrinterDefault) {
      if (~_.findIndex(printerList, { id: activePrinterDefault.id, name: activePrinterDefault.name })) {
        initialState.id = activePrinterDefault.id;
        initialState.name = activePrinterDefault.name;
      }
    }

    return initialState;
  });

  // State used only if quoteParams is provided in props
  const [expirationDate, setExpirationDate] = useState<string | null>(null);
  const [hidePartNumbers, setHidePartNumbers] = useState<boolean>(_.get(quoteParams, 'hidePartNumbers', false));

  // Control refs
  const applyButton = useRef<HTMLButtonElement>(null);
  const dateInput = useRef<HTMLInputElement>(null);
  const dayShortcutInput = useRef<HTMLInputElement>(null);
  const monthShortcutInput = useRef<HTMLInputElement>(null);
  const quoteTitleInput = useRef<HTMLInputElement>(null);
  const weekShortcutInput = useRef<HTMLInputElement>(null);

  // Memoized refs
  const today = useRef<number | null>(null);
  const pikaday = useRef<any>(null);

  useEffect(() => {
    // https://github.com/react-bootstrap/react-bootstrap/issues/2845
    setTimeout(async () => {
      if (quoteParams) {
        try {
          today.current = _.get(await Api.get('/shopkeeper/now'), 'data', Date.now());
        } catch (e) {
          console.error('Unable to fetch epoch from server, using local time.');
          today.current = Date.now();
        }

        // Sanity check
        if (quoteParams.defaultDays > quoteParams.maxDays) {
          quoteParams.defaultDays = quoteParams.maxDays;
        }

        pikaday.current = new Pikaday({
          defaultDate: new Date(today.current! + quoteParams.defaultDays * 86400000),
          field: dateInput.current,
          format: 'MM-DD-YY',
          maxDate: new Date(today.current! + quoteParams.maxDays * 86400000),
          minDate: new Date(today.current!),
          onSelect: () => {
            clearDateShortcuts();
            setExpirationDate(dateInput.current!.value);
          },
          setDefaultDate: true,
        });

        // Let pikaday format the date before we set it in the hook
        setExpirationDate(dateInput.current!.value);
      }
    }, 0);

    if (quoteParams) {
      return () => pikaday.current.destroy();
    }
  }, []);

  useEffect(() => {
    let timeout = 0;
    if (quoteParams && expirationDate !== null && (printerList.length > 0 ? activePrinter.id !== '' : true)) {
      // pikaday needs a bigger timeout to prevent re-popping calendar
      timeout = 100;
    }

    setTimeout(() => {
      // Skip autofocus if this is a quote and shortcut controls are in use
      if (
        quoteParams &&
        ((dayShortcutInput.current && dayShortcutInput.current.value !== '') ||
          (weekShortcutInput.current && weekShortcutInput.current.value !== '') ||
          (monthShortcutInput.current && monthShortcutInput.current.value !== ''))
      ) {
        return;
      }

      // Safer autofocus
      const el = ReactDOM.findDOMNode(applyButton.current!) as HTMLButtonElement;
      if (el) {
        el.focus();
      }
    }, timeout);
  });

  const calculateShortcutDate = () => {
    const days = parseInt(dayShortcutInput.current!.value, 10) || 0;
    const weeks = parseInt(weekShortcutInput.current!.value, 10) || 0;
    const months = parseInt(monthShortcutInput.current!.value, 10) || 0;

    const result = moment(today.current!).add(months, 'months').add(weeks, 'weeks').add(days, 'days');

    if (moment(result).isAfter(today.current! + quoteParams!.maxDays * 86400000)) {
      toastr.error('Error', `Max Quote Days: ${quoteParams!.maxDays}`);
    }

    // Pikaday applies min/max bounding rules accordingly, 2nd param suppresses onSelect
    pikaday.current.setDate(result.format('MM-DD-YY'), true);
    setExpirationDate(dateInput.current!.value);
  };

  const clearDateShortcuts = () => {
    dayShortcutInput.current!.value = '';
    monthShortcutInput.current!.value = '';
    weekShortcutInput.current!.value = '';
  };

  return (
    <Modal
      backdrop="static"
      container={container as any}
      dialogClassName="sk-printerSelectionModal"
      onHide={hide as any}
      show={show}
    >
      <Modal.Header closeButton={true}>
        <Modal.Title>Print Options</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {quoteParams && (
          <>
            <div className="form-group sk-processOption">
              <label>Quote Expiration Date</label>
              <div className="input-group" style={{ position: 'relative' }}>
                <div className="input-group-addon">
                  <i className="zmdi zmdi-calendar" style={{ fontSize: '18px' }} />
                </div>
                <input
                  type="text"
                  className="form-control sk-useMaterialInput"
                  onBlur={() => {
                    if (dateInput.current!.value.trim() === '') {
                      setExpirationDate(null);
                    }
                  }}
                  placeholder="MM-DD-YY"
                  ref={dateInput}
                />
              </div>
            </div>
            <div className="sk-processOption">
              <label>Quote Expiration Date Shortcuts (From Today)</label>
              <div className="sk-dateShortcuts">
                <div>
                  <input
                    type="number"
                    placeholder="N/A"
                    className="form-control sk-useMaterialInput"
                    min="0"
                    ref={dayShortcutInput}
                    onChange={calculateShortcutDate}
                  />
                  days
                </div>
                <div>
                  <input
                    type="number"
                    placeholder="N/A"
                    className="form-control sk-useMaterialInput"
                    min="0"
                    ref={weekShortcutInput}
                    onChange={calculateShortcutDate}
                  />
                  weeks
                </div>
                <div>
                  <input
                    type="number"
                    placeholder="N/A"
                    className="form-control sk-useMaterialInput"
                    min="0"
                    ref={monthShortcutInput}
                    onChange={calculateShortcutDate}
                  />
                  months
                </div>
                <div className="sk-clearDateShortcuts sk-actionLink" onClick={clearDateShortcuts}>
                  Clear Shortcuts
                </div>
              </div>
            </div>
            <div className="sk-processOption">
              <FormCheck inline={true}>
                <FormCheck.Input
                  type="checkbox"
                  id="hide-part-nums"
                  className="sk-useMaterialInput ml-1"
                  onChange={() => setHidePartNumbers(!hidePartNumbers)}
                  checked={hidePartNumbers}
                />
                <FormCheck.Label htmlFor="hide-part-nums">Hide Part Numbers on Quote</FormCheck.Label>
              </FormCheck>
            </div>
            <div className="sk-processOption">
              <label>Quote Title (Optional)</label>
              <input type="text" className="form-control sk-useMaterialInput" ref={quoteTitleInput} />
            </div>
          </>
        )}
        {printerList.length !== 0 && (
          <>
            <label>Printer Selection</label>
            <table className="table sk-printerTable">
              <tbody>
                {/* Show screen printer first */}
                {screenPrinterID && (
                  <tr
                    className={classNames({
                      'sk-printerSelected': screenPrinterID === activePrinter.id,
                      'sk-selectorRow': true,
                    })}
                    onClick={() => setActivePrinter({ id: screenPrinterID, name: 'Screen' })}
                  >
                    <td className="sk-printerType">
                      <i className="zmdi zmdi-tv" />
                    </td>
                    <td className="sk-printerName">Print to Screen</td>
                  </tr>
                )}

                {/* Followed by all other non-screen printers (CUPS) */}
                {printerList.map((printer, index) => {
                  if (printer.id === screenPrinterID) {
                    return null;
                  }

                  return (
                    <tr
                      key={`printer-${index}`}
                      className={classNames({
                        'sk-printerSelected': printer.id === activePrinter.id,
                        'sk-selectorRow': true,
                      })}
                      onClick={() => setActivePrinter({ id: printer.id, name: printer.name })}
                    >
                      <td className="sk-printerType">
                        <i className="zmdi zmdi-print" />
                      </td>
                      <td className="sk-printerName">{printer.name}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => hide()} variant="secondary">
          Cancel
        </Button>
        <Button
          variant="success"
          disabled={(printerList.length > 0 && activePrinter.id === '') || (quoteParams && expirationDate === null)}
          onClick={() => {
            if (quoteParams) {
              const quoteTitle = quoteTitleInput.current!.value.trim();

              // expirationDate & hidePartNumbers *should* be deterministically set by the UI, but just in case
              onPrinterSelection(
                activePrinter.id,
                activePrinter.name,
                expirationDate || '',
                hidePartNumbers || false,
                quoteTitle
              );
            } else {
              onPrinterSelection(activePrinter.id, activePrinter.name);
            }
          }}
          ref={applyButton}
        >
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PrintSelectionModal;
